import React, { useState } from "react";
import { Table, Button, Checkbox, Select } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import InputNumberField from "../../../../../components/Input/InputNumber";
import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import {
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  calculateTotals,
  setFooterValues,
  setPayServiceCart,
} from "../serviceExpenseSlice";
import { selectItems } from "../../../../../store/commonSelectors/lookupsSelectors";
import SharedFooter from "../../../../../components/DataTableFooter/SharedFooter";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { getCurrencySymbol } from "../../../../../lib/currency";
import { useNavigate } from "react-router-dom";
import ProductQuickDrawer from "../../../../Product_manage/components/drawers/ProductQuickDrawer";
import { fetchItems } from "../../../../../store/commonThunks/lookups";
import Big from "big.js";
import { getItemDisplayName } from "../../../../../utils";

const ServiceExpenseTable = ({ authInfo }) => {
  const currency = authInfo?.currency || "AED";
  const symbol = getCurrencySymbol(currency);
  const dispatch = useDispatch();
  const items = useSelector(selectItems);
  const navigate = useNavigate();
  const footerValues = useSelector((s) => s.serviceExpense.footerValues);
  const itemCart = useSelector((s) => s.serviceExpense.itemCart);
  const taxMode = useSelector((s) => s.serviceExpense.taxMode);
  const conversionRate = useSelector((s) => s.serviceExpense.conversionRate);
  const [productDrawer, setProductDrawer] = useState({ open: false, rowKey: null, });

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;

  const handleUpdate = (rowKey, field, value) => {
    dispatch(updateOrderRow({ rowKey, updates: { [field]: value } }));
    dispatch(calculateTotals({ authInfo }));
  };

  const handleAddRow = () => {
    const newRow = {
      key: Date.now(),
      item_id: null,
      product: "",
      quantity: null,
      rate: null,
      tax_per: 0,
      discount: null,
      discountAmt: null,
      discountMode: "percent",
      bonus: false,
    };

    dispatch(addOrderRow(newRow));
  };

  const handleFooterChange = (value, field) => {
    let updatedFooter = { ...footerValues };
    if (field === "payCart") {
      const newPaidAmount = value.reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0
      );

      updatedFooter = {
        ...updatedFooter,
        payCart: value,
        paid_amount: newPaidAmount,
        paidAmount: newPaidAmount,
        dueAmount: (
          parseFloat(updatedFooter.grandTotal || 0) - newPaidAmount
        ).toFixed(2),
      };

      // ✅ keep payservicecart in sync
      dispatch(setPayServiceCart(value));
    } else if (field === "paidAmount") {
      updatedFooter.paidAmount = Number(value) || 0;
      updatedFooter.dueAmount = (
        parseFloat(updatedFooter.grandTotal || 0) - updatedFooter.paidAmount
      ).toFixed(2);
    } else {
      updatedFooter[field] = value;
    }

    dispatch(setFooterValues(updatedFooter));
    dispatch(calculateTotals({ authInfo }));
  };

  const getFilteredServiceItems = (currentRow) => {
    return items.filter((it) => {
      if (it.is_service !== "yes") return false;

      return !itemCart.some((row) => {
        if (row.key === currentRow.key) return false;
        return row.item_id === it.item_id;
      });
    });
  };

  const baseColumns = [
    // {
    //   title: "Bonus",
    //   dataIndex: "bonus",
    //   render: (_, record) => (
    //     <Checkbox
    //       checked={record.bonus}
    //       onChange={(e) => handleUpdate(record.key, "bonus", e.target.checked)}
    //     />
    //   ),
    // },
    {
      title: "Expense Item",
      dataIndex: "product",
      render: (_, record) => (
        <SearchListAutoComplete
          entityType="item"
          placeholder="Expense Item"
          style={{ width: "100%" }}
          optionsList={getFilteredServiceItems(record).map((i) => ({
            sale_id: i.item_id,
            display_text: getItemDisplayName(i),
            raw: i,
          }))}
          value={
            record.item_id
              ? {
                // `product` is also what gets saved as this row's item_name,
                // so it stays the plain name -- the fuller
                // "name - code - barcode" label only lives in
                // product_display, used for showing/picking in this field.
                value: record.product_display || record.product,
                label: record.product_display || record.product,
                display_text: record.product_display || record.product,
                raw: record,
              }
              : null
          }
          onSelect={(obj) => {
            if (!obj) return;
            if (obj === "add_new") {
              setProductDrawer({
                open: true,
                rowKey: record.key,
              });
              return;
            }

            const it =
              items.find((i) => i.item_id === obj?.sale_id) || obj?.raw;
            // handleUpdate(record.key, "item_id", it.item_id);
            // handleUpdate(record.key, "product", it.item_name);
            // handleUpdate(record.key, "rate", it.purchase_rate ?? 0);
            // handleUpdate(record.key, "tax_per", it.tax_per ?? 0);
            // handleUpdate(record.key, "item_code", it.item_code ?? 0);
            const updates = {
              item_id: it.item_id,
              product: it.item_name,
              product_display: getItemDisplayName(it),
              item_name: it.item_name,
              item_code: it.item_code ?? "",
              rate: it.purchase_rate ?? 0,
              tax_per: it.tax_per ?? 0,
              unit_id: it.unit_id,
              unit_name: it.unit_name,
              unit_symbol: it.unit_symbol,
              per_unit_id: it.unit_id ?? 0,
              per_unit_symbol: it.unit_symbol || "",
              per_conversion: it.conversion || 1,
              warehouse_id: it.warehouse_id || 0,
              warehouse_name: it.warehouse_name || "",
              base_unit_name: it.base_unit_name || "",
              conversion: it.conversion || 1,
              units: it.units || [],
              is_service: it.is_service ?? "no",
              is_serial: it.is_serial ?? "no",
              serials: [],
            };
            dispatch(updateOrderRow({ rowKey: record.key, updates }));
            dispatch(calculateTotals({ authInfo }));
          }}
        />
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      render: (_, record) => (
        <InputNumberField
          min={0}
          max={100000}
          value={record.quantity}
          placeholder="1"
          onClick={() => {
            if (record.quantity !== null) {
              handleUpdate(record.key, "quantity", null);
            }
          }}
          onChange={(v) => handleUpdate(record.key, "quantity", v)}
          formatter={(value) => {
            if (!value && value !== 0) return "";
            return Math.floor(Number(value)).toString();
          }}
          parser={(value) => {
            if (!value) return 0;
            return parseInt(value.replace(/[^\d]/g, "")) || 0;
          }}
          onKeyPress={(e) => {
            if ([".", ","].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            // Prevent pasting decimal values
            const pasteData = e.clipboardData.getData("text");
            if (pasteData.includes(".") || pasteData.includes(",")) {
              e.preventDefault();
            }
          }}
        />
      ),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      render: (_, record) => (
        <InputNumberField
          min={0}
          precision={2}
          value={
            conversionRate
              ? Number(record?.rate || 0) * conversionRate
              : record?.rate
          }
          placeholder="0.00"
          onClick={() => {
            if (record.rate !== null) {
              handleUpdate(record.key, "rate", null);
            }
          }}
          onChange={(v) => handleUpdate(record.key, "rate", v)}
        />
      ),
    },
  ];

  const individualColumns = [
    {
      title: "Discount",
      dataIndex: "discount",
      width: 160,
      render: (_, record) => {
        const mode = record.discountMode === "amount" ? "amount" : "percent";
        const lineTotal = new Big(Number(record.quantity) || 0).times(Number(record.rate) || 0);
        // One merged compound field (number input + unit-select addon)
        // instead of a separate Select stacked above the number input.
        const modeSelect = (
          <Select
            size="small"
            style={{ width: 65 }}
            value={mode}
            options={[
              { label: "%", value: "percent" },
              { label: symbol, value: "amount" },
            ]}
            onChange={(v) => handleUpdate(record.key, "discountMode", v)}
          />
        );
        return mode === "amount" ? (
          <InputNumberField
            min={0}
            max={Number(lineTotal)}
            precision={2}
            value={record.discountAmt}
            onChange={(v) => handleUpdate(record.key, "discountAmt", v)}
            addonAfter={modeSelect}
          />
        ) : (
          <InputNumberField
            min={0}
            max={100}
            precision={2}
            value={record.discount}
            onClick={() => {
              if (record.discount !== null) {
                handleUpdate(record.key, "discount", null);
              }
            }}
            onChange={(v) => handleUpdate(record.key, "discount", v)}
            addonAfter={modeSelect}
          />
        );
      },
    },
  ];

  const gstColumn = showTax
    ? [
      {
        title: `${taxLabel} %`,
        dataIndex: "tax_per",
        render: (_, record) => (
          <InputNumberField
            min={0}
            max={100}
            precision={2}
            value={record.tax_per}
            onChange={(v) => handleUpdate(record.key, "tax_per", v)}
            disabled
          />
        ),
      },
    ]
    : [];

  const amountColumn = [
    {
      title: "Line Total",
      render: (_, r) => {
        if (r.bonus) return "0.00";
        const qty = Big(r.quantity || 0);
        const rate = Big(r.rate || 0);

        let line = qty.times(rate);

        if (taxMode === "on_item") {
          const discountMode = r.discountMode === "amount" ? "amount" : "percent";
          let discountAmt;
          if (discountMode === "amount") {
            const raw = Big(r.discountAmt || 0);
            discountAmt = raw.gt(line) ? line : raw.lt(0) ? Big(0) : raw;
          } else {
            discountAmt = line.times(Big(r.discount || 0)).div(100);
          }
          line = line.minus(discountAmt);

          const taxAmt = showTax ? line.times(Big(r.tax_per || 0)).div(100) : Big(0);
          line = line.plus(taxAmt);
        }
        const convertedLine = parseFloat(
          conversionRate ? line.times(conversionRate) : line
        ).toFixed(2);

        return convertedLine;
      },
    },
    {
      title: "",
      render: (_, r) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => dispatch(deleteOrderRow(r.key))}
          disabled={itemCart.length === 1}
        />
      ),
    },
  ];

  const columns =
    taxMode === "on_item"
      ? [...baseColumns, ...individualColumns, ...gstColumn, ...amountColumn]
      : [...baseColumns, ...amountColumn];

  return (
    <div>
      <Button
        type="dashed"
        disabled={productDrawer.open}
        onClick={handleAddRow}
        icon={<PlusOutlined />}
        style={{ marginBottom: 8 }}
      >
        Add Row
      </Button>
      <div style={{ width: "100%", maxWidth: "1300px" }}>
        <Table
          dataSource={itemCart}
          columns={columns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "min-content" }}
          footer={() => (
            <SharedFooter
              footerValues={footerValues}
              dataSource={itemCart}
              onFooterChange={handleFooterChange}
              pageType="serviceExpense"
              taxMode={taxMode}
              conversionRate={conversionRate}
            />
          )}
        />
      </div>

      <ProductQuickDrawer
        open={productDrawer.open}
        onClose={() => setProductDrawer({ open: false, rowKey: null })}
        onCreated={(product) => {
          if (!product) return;

          // 🔒 Capture rowKey BEFORE clearing state
          const rowKey = productDrawer.rowKey;

          // 1️⃣ Close drawer
          setProductDrawer({ open: false, rowKey: null });

          // 2️⃣ Refresh product list (for future searches)
          dispatch(fetchItems());

          // 3️⃣ Use CREATED PRODUCT DIRECTLY (no find, no delay)
          const updates = {
            item_id: product.item_id,
            product: product.item_name,
            product_display: getItemDisplayName(product),
            item_name: product.item_name,
            item_code: product.item_code ?? "",
            rate: product.purchase_rate ?? 0,
            tax_per: product.tax_per ?? 0,
            unit_id: product.unit_id,
            unit_name: product.unit_name,
            unit_symbol: product.unit_symbol,
            per_unit_id: product.unit_id ?? 0,
            per_unit_symbol: product.unit_symbol || "",
            per_conversion: product.conversion || 1,
            warehouse_id: product.warehouse_id || 0,
            warehouse_name: product.warehouse_name || "",
            base_unit_name: product.base_unit_name || "",
            conversion: product.conversion || 1,
            units: product.units || [],
            is_service: product.is_service ?? "no",
            is_serial: product.is_serial ?? "no",
            serials: [],
          };

          // 4️⃣ Update EXACT row
          dispatch(updateOrderRow({ rowKey, updates }));
          dispatch(calculateTotals({ authInfo }));
        }}
      />


    </div>
  );
};

export default ServiceExpenseTable;
